// MACHINERY V2 (Stroje) — the breadcrumb both SR pages wear (M2.6).
//
// Spec: data package §7 ("breadcrumb tail" row) — `breadcrumbEquipment` >
// machine display name > create/edit — plus the SR-detail trail, which adds the
// Servisné správy hop and ends in the mono report number. Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `SrBreadcrumb` (lines 295-315);
// artboards 0700-0703 all draw the same strip.
//
// It matches the v2 PROFILE breadcrumb (profile/ProfileBody.tsx:90-130) rather
// than the v1 SR one: same 10px/24px band, same border-soft rule underneath,
// same accent links and `›` separators. Both SR pages sit under the profile in
// the hierarchy, so they must not look like a different application.
//
// Every hop targets the V2 surface. `/machinery` stays byte-untouched and
// unreferenced from here — the only v2 SR link that deliberately points at a
// live page is the solve wizard (§9 D5), and it is not a breadcrumb.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { useNavigate } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MACHINERY_V2_BASE } from "./srNavigation";

export interface SrPageBreadcrumbProps {
  machineId: string;
  /** Empty while the machine is still loading — the hop then shows "Stroj". */
  machineName: string;
  /** Already-translated tail (create/edit label, or `#SR-…`). */
  current: string;
  /** Renders the tail in mono — the report number on the detail page. */
  currentMono?: boolean;
}

const linkStyle: React.CSSProperties = {
  padding: 0,
  border: 0,
  background: "transparent",
  cursor: "pointer",
  font: "inherit",
  color: "var(--mod-accent)",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const Sep: React.FC = () => (
  <span style={{ color: "var(--faint)", flexShrink: 0 }} aria-hidden>
    ›
  </span>
);

const SrPageBreadcrumb: React.FC<SrPageBreadcrumbProps> = ({
  machineId,
  machineName,
  current,
  currentMono,
}) => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();
  const profilePath = `${MACHINERY_V2_BASE}/${machineId}`;

  return (
    <nav
      aria-label={translate("myMachinery:breadcrumbEquipment")}
      style={{
        padding: "10px 24px",
        borderBottom: "1px solid var(--border-soft)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12.5,
        color: "var(--muted)",
        minWidth: 0,
        flexWrap: "wrap",
      }}
    >
      <button type="button" style={linkStyle} onClick={() => navigate(MACHINERY_V2_BASE)}>
        {translate("myMachinery:breadcrumbEquipment")}
      </button>
      <Sep />
      <button
        type="button"
        style={{ ...linkStyle, maxWidth: 220 }}
        onClick={() => navigate(profilePath)}
        title={machineName || undefined}
      >
        {machineName || translate("myMachinery:machine")}
      </button>
      <Sep />
      <button type="button" style={linkStyle} onClick={() => navigate(profilePath)}>
        {translate("myMachinery:serviceReportsTab")}
      </button>
      <Sep />
      <span
        style={{
          color: "var(--ink-2)",
          fontWeight: 500,
          fontFamily: currentMono ? "var(--font-mono)" : "inherit",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {current}
      </span>
    </nav>
  );
};

export default SrPageBreadcrumb;
